import { useMemo, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import { FlaskConical, Plus, RefreshCw } from 'lucide-react';
import PageHeader from '@/components/PageHeader.jsx';
import { useToast } from '@/hooks/useKeyboardShortcuts.js';
import { LIST_POLITICAS, ACTIVAR_POLITICA, SIMULAR_POLITICA } from './gql.js';
import PoliticaForm from './Form.jsx';

const moduloLabels = {
  ventas: 'Ventas',
  compras: 'Compras',
  tesoreria: 'Tesorería',
  stock: 'Stock',
};

export default function PoliticasList() {
  const toast = useToast();
  const [empresaId, setEmpresaId] = useState('1');
  const [modulo, setModulo] = useState('');
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState('view');
  const [{ data, fetching }, reexecute] = useQuery({
    query: LIST_POLITICAS,
    variables: { empresaId, modulo: modulo || null },
    pause: !empresaId,
  });
  const [, activarPolitica] = useMutation(ACTIVAR_POLITICA);
  const [, simularPolitica] = useMutation(SIMULAR_POLITICA);
  const politicas = data?.listPoliticas ?? [];

  const handleRefresh = () => {
    reexecute({ requestPolicy: 'network-only' });
  };

  const handleToggle = async (politica) => {
    try {
      const result = await activarPolitica({ id: politica.id, activo: !politica.activo });
      if (result.error) throw result.error;
      toast.success(!politica.activo ? 'Política activada' : 'Política desactivada');
      handleRefresh();
    } catch (error) {
      toast.error(error.message ?? 'No fue posible actualizar la política');
    }
  };

  const handleSimular = async (politica) => {
    const importe = Number(window.prompt('Importe para simular', '10000'));
    if (!Number.isFinite(importe) || importe <= 0) {
      toast.error('Importe inválido');
      return;
    }
    try {
      const result = await simularPolitica({
        empresaId,
        modulo: politica.modulo,
        evento: politica.evento,
        importe,
      });
      if (result.error) throw result.error;
      const detalle = result.data?.simularPolitica?.asiento?.detalle ?? [];
      const resumen = detalle.map((linea) => `${linea.cuentaCodigo}: D ${linea.debe?.toFixed(2)} / H ${linea.haber?.toFixed(2)}`).join('\n');
      toast.info(`Pre-asiento generado:\n${resumen}`);
    } catch (error) {
      toast.error(error.message ?? 'No fue posible simular');
    }
  };

  return (
    <div className="min-h-screen bg-[rgba(var(--bg),0.95)] pb-16">
      <div className="mx-auto max-w-[1400px] px-4 pb-10 pt-8">
        <PageHeader
          title="Políticas de Imputación"
          subtitle="Definí cómo se generan los asientos automáticos"
          actions={[
            <button
              key="refresh"
              type="button"
              onClick={handleRefresh}
              className="flex items-center gap-2 rounded-xl border border-[rgb(var(--border))] px-3 py-2 text-sm text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            >
              <RefreshCw className="h-4 w-4" />
              Actualizar
            </button>,
            <button
              key="create"
              type="button"
              onClick={() => setMode('create')}
              className="flex items-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[rgb(var(--brand-700))] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(var(--ring))]"
            >
              <Plus className="h-4 w-4" />
              Nueva política
            </button>,
          ]}
        />

        <div className="grid grid-cols-12 gap-6">
          <section className="col-span-12 md:col-span-6 space-y-4">
            <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.85)] p-4 shadow-[0_18px_34px_-20px_rgba(var(--shadow),0.45)]">
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-12">
                  <label className="text-xs font-medium uppercase text-[rgb(var(--muted))]">Empresa</label>
                  <input
                    type="number"
                    min="1"
                    value={empresaId}
                    onChange={(event) => setEmpresaId(event.target.value)}
                    className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.9)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  />
                </div>
                <div className="col-span-12">
                  <label className="text-xs font-medium uppercase text-[rgb(var(--muted))]">Módulo</label>
                  <select
                    value={modulo}
                    onChange={(event) => setModulo(event.target.value)}
                    className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.9)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  >
                    <option value="">Todos</option>
                    {Object.entries(moduloLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.8)] shadow-[0_18px_34px_-20px_rgba(var(--shadow),0.45)]">
              <header className="flex items-center justify-between border-b border-[rgb(var(--border))] px-4 py-3 text-sm text-[rgb(var(--muted))]">
                <span>{fetching ? 'Cargando políticas...' : `${politicas.length} políticas`}</span>
              </header>
              <div className="max-h-[580px] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-[rgba(var(--panel),0.95)] text-[rgb(var(--muted))]">
                    <tr>
                      <th className="px-4 py-2 text-left">Módulo</th>
                      <th className="px-4 py-2 text-left">Evento</th>
                      <th className="px-4 py-2 text-left">Debe</th>
                      <th className="px-4 py-2 text-left">Haber</th>
                      <th className="px-4 py-2 text-left">Centro</th>
                      <th className="px-4 py-2 text-left">Estado</th>
                      <th className="px-4 py-2 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgb(var(--border))]">
                    {politicas.map((politica) => (
                      <tr key={politica.id} className="transition-colors hover:bg-[rgba(var(--muted),0.12)]">
                        <td className="px-4 py-3 font-medium text-[rgb(var(--fg))]">{moduloLabels[politica.modulo] || politica.modulo}</td>
                        <td className="px-4 py-3">{politica.evento}</td>
                        <td className="px-4 py-3 text-[rgb(var(--muted))]">
                          {politica.cuentaDebeCodigo ? `${politica.cuentaDebeCodigo} · ${politica.cuentaDebeNombre}` : '—'}
                        </td>
                        <td className="px-4 py-3 text-[rgb(var(--muted))]">
                          {politica.cuentaHaberCodigo ? `${politica.cuentaHaberCodigo} · ${politica.cuentaHaberNombre}` : '—'}
                        </td>
                        <td className="px-4 py-3 text-[rgb(var(--muted))]">{politica.centroNombre || '—'}</td>
                        <td className="px-4 py-3 text-[rgb(var(--muted))]">{politica.activo ? 'Activa' : 'Inactiva'}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setSelected(politica);
                                setMode('edit');
                              }}
                              className="rounded-xl border border-[rgb(var(--border))] px-3 py-1 text-xs text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleToggle(politica)}
                              className="rounded-xl border border-[rgb(var(--border))] px-3 py-1 text-xs text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                            >
                              {politica.activo ? 'Desactivar' : 'Activar'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSimular(politica)}
                              className="flex items-center gap-1 rounded-xl border border-[rgb(var(--border))] px-3 py-1 text-xs text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                            >
                              <FlaskConical className="h-3 w-3" />
                              Simular
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!politicas.length && !fetching ? (
                  <div className="px-4 py-6 text-center text-sm text-[rgb(var(--muted))]">No hay políticas configuradas.</div>
                ) : null}
              </div>
            </div>
          </section>

          <section className="col-span-12 md:col-span-6 space-y-4">
            {mode === 'create' ? (
              <PoliticaForm empresaId={empresaId} onSuccess={() => { setMode('view'); handleRefresh(); }} onCancel={() => setMode('view')} />
            ) : mode === 'edit' && selected ? (
              <PoliticaForm
                empresaId={empresaId}
                politica={selected}
                onSuccess={() => {
                  setMode('view');
                  handleRefresh();
                }}
                onCancel={() => setMode('view')}
              />
            ) : (
              <div className="rounded-2xl border border-dashed border-[rgb(var(--border))] bg-[rgba(var(--panel),0.4)] p-6 text-center text-sm text-[rgb(var(--muted))]">
                Seleccioná una política o creá una nueva para ver el detalle.
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
